import { oc } from "@orpc/contract";
import {
  SheetNameInputSchema,
  WorkbookSheetsResponseFields,
} from "./workbook-preview.contract";
import { z } from "zod";
import {
  AgentChannelSchema,
  DiscountElementTargetType,
  DiscountElementType,
  DiscountGroupStatus,
  DraftLineDiscountStatusV2,
} from "@erp/shared-types";

/**
 * Discounts v2 contracts — the OMS v3 discount system.
 *
 * Groups (exception price lists / bulk batches / SPRs) hold elements; an
 * engine matches elements to draft sales-order lines and stacks them in a
 * deterministic application order. See `.claude/skills/discounts`.
 */

const UUIDSchema = z.uuid();

const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

export const DiscountElementTypeSchema = z.nativeEnum(DiscountElementType);
export const DiscountElementTargetTypeSchema = z.nativeEnum(
  DiscountElementTargetType,
);
export const DiscountGroupStatusSchema = z.nativeEnum(DiscountGroupStatus);
export const DraftLineDiscountStatusV2Schema = z.nativeEnum(
  DraftLineDiscountStatusV2,
);

/** Money/quantity values cross the wire as canonical decimal strings. */
const DecimalStringSchema = z.string();

// ─── Element / group DTOs ───────────────────────────────────────────────────

export const DiscountElementV2Schema = z.object({
  id: UUIDSchema,
  groupId: UUIDSchema.nullable(),
  customerId: UUIDSchema.nullable(),
  discountType: DiscountElementTypeSchema,
  discountValue: DecimalStringSchema,
  targetType: DiscountElementTargetTypeSchema,
  targetValue: z.string().nullable(),
  /** Human-readable target (resolved SKU for variant targets). */
  targetLabel: z.string().nullable(),
  effectiveStart: DateOnlySchema.nullable(),
  effectiveEnd: DateOnlySchema.nullable(),
  isActive: z.boolean(),
  isSingleUse: z.boolean(),
  isConsumed: z.boolean(),
  maxQuantity: DecimalStringSchema.nullable(),
  usedQuantity: DecimalStringSchema,
  createdAt: z.iso.datetime(),
});
export type DiscountElementV2 = z.infer<typeof DiscountElementV2Schema>;

export const DiscountGroupV2Schema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  referenceCode: z.string().nullable(),
  /** Generated shareable code (e.g. "X7K9-Q24M") a customer can quote. */
  discountCode: z.string().nullable(),
  status: DiscountGroupStatusSchema,
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  attachment: z.string().nullable(),
  createdByName: z.string().nullable(),
  elementCount: z.number().int(),
  activeElementCount: z.number().int(),
  /** True when metadata.review still has unresolved customer/lines. */
  needsReview: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type DiscountGroupV2 = z.infer<typeof DiscountGroupV2Schema>;

// ─── Review (ingested-group fix-up) ─────────────────────────────────────────

export const DiscountGroupReviewCandidateSchema = z.object({
  productVariantId: UUIDSchema,
  confidence: z.number(),
  sku: z.string().nullable(),
  label: z.string().nullable(),
});

export const DiscountGroupReviewLineSchema = z.object({
  key: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  discountType: DiscountElementTypeSchema,
  discountValue: z.number(),
  maxQuantity: z.number().nullable(),
  effectiveStart: z.string().nullable(),
  effectiveEnd: z.string().nullable(),
  unresolvedReason: z.enum(["sku_not_matched", "no_price_or_percent"]),
  candidates: z.array(DiscountGroupReviewCandidateSchema),
  /** Placeholder element already created for a matched-but-priceless row. */
  elementId: UUIDSchema.optional(),
});
export type DiscountGroupReviewLineDTO = z.infer<
  typeof DiscountGroupReviewLineSchema
>;

export const DiscountGroupReviewSchema = z.object({
  suggestedCustomer: z
    .object({
      customerId: UUIDSchema,
      customerName: z.string(),
      score: z.number(),
    })
    .optional(),
  unresolvedCustomerName: z.string().nullable().optional(),
  unresolvedLines: z.array(DiscountGroupReviewLineSchema),
  dismissedLines: z.array(DiscountGroupReviewLineSchema).optional(),
});
export type DiscountGroupReviewDTO = z.infer<typeof DiscountGroupReviewSchema>;

export const DiscountGroupV2DetailSchema = DiscountGroupV2Schema.extend({
  elements: z.array(DiscountElementV2Schema),
  /** Human fix-up state for ingested groups; null once everything resolved. */
  review: DiscountGroupReviewSchema.nullable(),
  /**
   * Sender of the email this request was ingested from, when there was one —
   * drives the "Approve & Email" reply-to-requester flow. Null for manually
   * created / uploaded groups.
   */
  requesterEmail: z.string().nullable(),
});
export type DiscountGroupV2Detail = z.infer<typeof DiscountGroupV2DetailSchema>;

// ─── Group CRUD ──────────────────────────────────────────────────────────────

/** Sortable columns on the Discounts page group list (server-side sort). */
export const DiscountGroupSortBySchema = z.enum([
  "name",
  "discountCode",
  "customerName",
  "status",
  "createdByName",
  "createdAt",
]);
export type DiscountGroupSortBy = z.infer<typeof DiscountGroupSortBySchema>;

export const DiscountGroupSortDirectionSchema = z.enum(["asc", "desc"]);

export const ListDiscountGroupsInputSchema = z.object({
  search: z.string().optional(),
  status: z.array(DiscountGroupStatusSchema).optional(),
  customerId: UUIDSchema.optional(),
  /** Only groups created by this user. */
  createdBy: UUIDSchema.optional(),
  /** Defaults to newest-first when omitted. */
  sortBy: DiscountGroupSortBySchema.optional(),
  sortDirection: DiscountGroupSortDirectionSchema.optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

export const ListDiscountGroupsResponseSchema = z.object({
  groups: z.array(DiscountGroupV2Schema),
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  }),
});

export const GetDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** Source-email descriptor for the review page's Emails rail tab. */
export const GroupSourceEmailThreadSchema = z.object({
  /** Whether the group was created from an inbound email. */
  hasSourceEmail: z.boolean(),
  /** Graph conversation/thread id — feeds the email-thread viewer. */
  conversationId: z.string().nullable(),
  /** The source inbound email id (for highlighting in the thread). */
  sourceEmailId: UUIDSchema.nullable(),
  /** Sender of the source email (seeds the reply composer's To). */
  fromAddress: z.string().nullable(),
  /** Subject of the source email. */
  subject: z.string().nullable(),
});

// ─── Activity (per-group action timeline) ───────────────────────────────────

/** One recorded action on a group — creation, edits, status changes, … */
export const DiscountGroupActivityEntrySchema = z.object({
  /** Namespaced kind, e.g. "discount_group.approved". */
  kind: z.string(),
  at: z.iso.datetime(),
  actorUserId: UUIDSchema.nullable(),
  /** Resolved user name, or a system-actor label ("Approval workflow"). */
  actorName: z.string().nullable(),
  message: z.string().nullable(),
  /** Per-field diff: field → [before, after] (ids resolved to names). */
  valuesChanged: z.record(z.string(), z.unknown()).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  /** Present when the user made this change through Nami ("via Nami"). */
  via: AgentChannelSchema.nullable(),
});
export type DiscountGroupActivityEntry = z.infer<
  typeof DiscountGroupActivityEntrySchema
>;

export const GetDiscountGroupActivityResponseSchema = z.object({
  entries: z.array(DiscountGroupActivityEntrySchema),
});

// ─── Usage (which sales orders a group's / element's discounts were applied to)

/** At least one of the two ids must be provided (refined in the handler). */
export const GetDiscountUsageInputSchema = z.object({
  discountGroupId: UUIDSchema.optional(),
  discountElementId: UUIDSchema.optional(),
});

/** One sales order that had this discount frozen onto at least one of its lines. */
export const DiscountUsageRowSchema = z.object({
  salesOrderId: UUIDSchema,
  /** The originating draft, for deep-linking to the sales-order-v3 page. */
  draftSalesOrderId: UUIDSchema.nullable(),
  /** Auto-increment human order number. */
  internalId: z.number().int(),
  customerPurchaseOrderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  orderDate: DateOnlySchema.nullable(),
  totalAmount: DecimalStringSchema.nullable(),
  /** How many of this order's lines carry one of the queried elements. */
  lineCount: z.number().int(),
});
export type DiscountUsageRow = z.infer<typeof DiscountUsageRowSchema>;

export const GetDiscountUsageResponseSchema = z.object({
  usage: z.array(DiscountUsageRowSchema),
});

// ─── Flat element list (the "Elements" view of the Discounts page) ──────────

/** One element row with its group context, for the flat elements table. */
export const DiscountElementListItemSchema = DiscountElementV2Schema.extend({
  groupName: z.string().nullable(),
  groupStatus: DiscountGroupStatusSchema.nullable(),
});
export type DiscountElementListItem = z.infer<
  typeof DiscountElementListItemSchema
>;

export const ListDiscountElementsInputSchema = z.object({
  /** Matches target SKU / segment value / group name or reference. */
  search: z.string().optional(),
  /** Elements that would apply to this customer (scoped to it or unscoped). */
  customerId: UUIDSchema.optional(),
  status: z.enum(["active", "inactive", "all"]).optional(),
  /** Only elements of this discount type. */
  discountType: DiscountElementTypeSchema.optional(),
  /**
   * PRODVIEW classification filters — same levels as the Product Catalog's
   * Family/Group/Model dropdowns (segments 1..3 of the dot-separated PRODVIEW).
   * Matches segment-targeted elements directly and variant-targeted elements
   * via their variant's PRODVIEW.
   */
  family: z.string().optional(),
  group: z.string().optional(),
  model: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

export const ListDiscountElementsResponseSchema = z.object({
  elements: z.array(DiscountElementListItemSchema),
  pagination: z.object({
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  }),
});

export const CreateDiscountElementInputSchema = z
  .object({
    discountType: DiscountElementTypeSchema,
    /**
     * Bps integer (e.g. 2050 = 20.50%) for PERCENT_BPS_OFF; currency amount
     * for AMOUNT_OFF / FIXED_PRICE.
     */
    discountValue: z.number().nonnegative(),
    targetType: DiscountElementTargetTypeSchema,
    /** Variant id / PRODVIEW segment; omit for ALL_PRODUCTS. */
    targetValue: z.string().trim().min(1).nullable().optional(),
    customerId: UUIDSchema.nullable().optional(),
    effectiveStart: DateOnlySchema.nullable().optional(),
    /** Every discount must expire — open-ended elements are not allowed. */
    effectiveEnd: DateOnlySchema,
    isSingleUse: z.boolean().optional(),
    maxQuantity: z.number().positive().nullable().optional(),
  })
  .superRefine((el, ctx) => {
    if (
      el.targetType === DiscountElementTargetType.ALL_PRODUCTS &&
      el.targetValue
    ) {
      ctx.addIssue({
        code: "custom",
        message: "ALL_PRODUCTS elements must not carry a targetValue",
        path: ["targetValue"],
      });
    }
    if (
      el.targetType !== DiscountElementTargetType.ALL_PRODUCTS &&
      !el.targetValue
    ) {
      ctx.addIssue({
        code: "custom",
        message: `${el.targetType} elements require a targetValue`,
        path: ["targetValue"],
      });
    }
    if (
      el.discountType === DiscountElementType.PERCENT_BPS_OFF &&
      (!Number.isInteger(el.discountValue) ||
        el.discountValue <= 0 ||
        el.discountValue > 10000)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "PERCENT_BPS_OFF requires an integer bps in (0, 10000]",
        path: ["discountValue"],
      });
    }
    if (
      el.effectiveStart &&
      el.effectiveEnd &&
      el.effectiveEnd < el.effectiveStart
    ) {
      ctx.addIssue({
        code: "custom",
        message: "effectiveEnd must be on or after effectiveStart",
        path: ["effectiveEnd"],
      });
    }
  });
export type CreateDiscountElementInput = z.infer<
  typeof CreateDiscountElementInputSchema
>;

export const CreateDiscountGroupInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).nullable().optional(),
  referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
  attachment: z.string().nullable().optional(),
  elements: z.array(CreateDiscountElementInputSchema).min(1),
  /** Submit straight to approval instead of leaving the group in DRAFT. */
  submitForApproval: z.boolean().optional(),
  /**
   * Create APPROVED with active elements — for flows where the creator has
   * the authority (e.g. the sales-order review screen, where the order itself
   * goes through approval). Mutually exclusive with `submitForApproval`.
   */
  approveImmediately: z.boolean().optional(),
});
export type CreateDiscountGroupInput = z.infer<
  typeof CreateDiscountGroupInputSchema
>;

export const CreateDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
});

export const SubmitGroupForApprovalInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** Direct RBAC-backed decision (requires the `approve` action on DiscountV2). */
export const DecideDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
  decision: z.enum([
    DiscountGroupStatus.APPROVED,
    DiscountGroupStatus.REJECTED,
  ]),
});

export const DecideDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  activatedElementCount: z.number().int(),
});

export const SubmitGroupForApprovalResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
});

/**
 * One-click "Approve & Email": approve + reply to the requester with the
 * approved form attached, Cc'ing the approver. Fails without approving when
 * the org has no discounts default sending mailbox configured.
 */
export const ApproveAndEmailDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const ApproveAndEmailDiscountGroupResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  activatedElementCount: z.number().int(),
  /** Requester address the approval email was sent to. */
  emailedTo: z.string(),
  /** Everyone Cc'd (discounts default Cc list + the approver). */
  cc: z.array(z.string()),
});

export const DeactivateDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const DeleteDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const RevertDiscountGroupToDraftInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

/** A generated Excel workbook, base64-encoded for the JSON transport. */
export const DiscountFormFileSchema = z.object({
  excelBase64: z.string(),
  filename: z.string(),
});

export const GenerateApprovedDiscountFormInputSchema = z.object({
  discountGroupId: UUIDSchema,
});

export const GenerateApprovedDiscountFormResponseSchema =
  DiscountFormFileSchema.extend({
    /**
     * Best-known requester address to prefill the compose panel with — the
     * ingested email's sender when the group came in by email, else null.
     */
    suggestedRecipient: z.string().nullable(),
    /**
     * The generated form persisted as a document (associated to the group) —
     * lets the frontend render it with the real Office spreadsheet viewer.
     * Null when the upload failed: the workbook still comes back inline, so
     * the preview falls back to the in-browser renderer.
     */
    documentId: UUIDSchema.nullable(),
  });

export const DeactivateDiscountElementInputSchema = z.object({
  discountElementId: UUIDSchema,
});

/** Element editing (review page): allowed only on DRAFT/REJECTED groups. */
export const AddDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  element: CreateDiscountElementInputSchema,
});
export const AddDiscountElementResponseSchema = z.object({
  discountElementId: UUIDSchema,
});

export const UpdateDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  discountElementId: UUIDSchema,
  element: CreateDiscountElementInputSchema,
});

export const DeleteDiscountElementInputSchema = z.object({
  discountGroupId: UUIDSchema,
  discountElementId: UUIDSchema,
});

export const SimpleSuccessResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateDiscountGroupInputSchema = z.object({
  discountGroupId: UUIDSchema,
  patch: z
    .object({
      name: z.string().trim().min(1).max(255).optional(),
      description: z.string().trim().max(2000).nullable().optional(),
      referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
      customerId: UUIDSchema.nullable().optional(),
    })
    .refine((patch) => Object.keys(patch).length > 0, {
      message: "Patch must contain at least one field",
    }),
});

export const ResolveUnresolvedLineInputSchema = z.object({
  discountGroupId: UUIDSchema,
  lineKey: z.string().min(1),
  productVariantId: UUIDSchema,
});

export const DismissUnresolvedLineInputSchema = z.object({
  discountGroupId: UUIDSchema,
  lineKey: z.string().min(1),
});

/** Give a matched-but-priceless ingested row its fixed price. */
export const ResolveUnresolvedLinePriceInputSchema = z.object({
  discountGroupId: UUIDSchema,
  lineKey: z.string().min(1),
  /**
   * Unit fixed price in the group's currency. Elements persist at 4 decimals,
   * so the floor is 0.0001 — anything smaller would store as $0 again.
   */
  fixedPrice: z
    .union([
      z.number(),
      z
        .string()
        .trim()
        .regex(/^\d+(\.\d+)?$/),
    ])
    .refine((v) => Number.isFinite(Number(v)) && Number(v) >= 0.0001, {
      message: "fixedPrice must be at least 0.0001",
    }),
});

export const ParsePriceListUploadInputSchema = z.object({
  /** An uploaded + confirmed document (xlsx/xls/csv). */
  documentId: UUIDSchema,
  /** Worksheet to read (from `previewPriceListUpload`); default first. */
  sheetName: SheetNameInputSchema,
  name: z.string().trim().min(1).max(255).nullable().optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  referenceCode: z.string().trim().min(1).max(255).nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
  effectiveStart: DateOnlySchema.nullable().optional(),
  /** Applied to every created element; discounts must expire. */
  effectiveEnd: DateOnlySchema,
  /**
   * Explicit column picks (from `previewPriceListUpload`). When present the
   * two columns are read deterministically; when absent the AI parser
   * auto-detects the SKU + price columns.
   */
  columnMapping: z
    .object({
      skuColumnIndex: z.number().int().min(0),
      priceColumnIndex: z.number().int().min(0),
    })
    .nullable()
    .optional(),
  /** Submit straight to approval when nothing needs review. */
  submitForApproval: z.boolean().optional(),
  /**
   * Create APPROVED with active elements when nothing needs review ("Save
   * And Activate"). Mutually exclusive with submitForApproval.
   */
  approveImmediately: z.boolean().optional(),
});
export type ParsePriceListUploadInput = z.infer<
  typeof ParsePriceListUploadInputSchema
>;

export const PreviewPriceListUploadInputSchema = z.object({
  documentId: UUIDSchema,
  sheetName: SheetNameInputSchema,
});
export type PreviewPriceListUploadInput = z.infer<
  typeof PreviewPriceListUploadInputSchema
>;

/** Headers + sample rows of an uploaded sheet, for the column picker. */
export const PreviewPriceListUploadResponseSchema = z.object({
  ...WorkbookSheetsResponseFields,
  headers: z.array(z.string()),
  sampleRows: z.array(z.array(z.string())),
  totalDataRows: z.number().int(),
  suggestedItemNoColumn: z.number().int().nullable(),
  suggestedPriceColumn: z.number().int().nullable(),
});
export type PreviewPriceListUploadResponse = z.infer<
  typeof PreviewPriceListUploadResponseSchema
>;

export const ParsePriceListUploadResponseSchema = z.object({
  discountGroupId: UUIDSchema,
  status: DiscountGroupStatusSchema,
  elementCount: z.number().int(),
  unresolvedCount: z.number().int(),
  /** SKUs (as written in the sheet) that could not be matched to a product. */
  unresolvedSkus: z.array(z.string()),
  /** True when the sheet exceeded the row cap and was cut off. */
  truncated: z.boolean(),
});
export type ParsePriceListUploadResponse = z.infer<
  typeof ParsePriceListUploadResponseSchema
>;

// ─── Draft-line candidates / stacks ─────────────────────────────────────────

export const DiscountCandidateV2Schema = DiscountElementV2Schema.extend({
  groupName: z.string().nullable(),
  groupStatus: DiscountGroupStatusSchema.nullable(),
  /** True when the element's group is still awaiting approval. */
  pending: z.boolean(),
  /** Discount request end customer (group metadata), when the grant names one. */
  endCustomerName: z.string().nullable(),
});
export type DiscountCandidateV2 = z.infer<typeof DiscountCandidateV2Schema>;

export const DraftLineCandidatesSchema = z.object({
  draftLineId: UUIDSchema,
  candidates: z.array(DiscountCandidateV2Schema),
  /**
   * Element the engine would auto-apply (a single active FIXED_PRICE candidate
   * exactly matching the PO unit price), when one exists.
   */
  autoApplyElementId: UUIDSchema.nullable(),
});

export const ListDraftCandidatesInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const ListDraftCandidatesResponseSchema = z.object({
  found: z.boolean(),
  lines: z.array(DraftLineCandidatesSchema),
});

export const DiscountStackTraceEntrySchema = z.object({
  elementId: UUIDSchema,
  applicationOrder: z.number().int(),
  discountType: DiscountElementTypeSchema,
  discountValue: DecimalStringSchema,
  unitPriceBefore: DecimalStringSchema,
  unitPriceAfter: DecimalStringSchema,
  discountAmount: DecimalStringSchema,
});

export const DraftLineStackSchema = z.object({
  draftLineDiscountId: UUIDSchema,
  draftLineId: UUIDSchema,
  totalDiscountAmount: DecimalStringSchema,
  finalUnitPrice: DecimalStringSchema.nullable(),
  /** True when any element's group is still awaiting approval. */
  pending: z.boolean(),
  elements: z.array(DiscountCandidateV2Schema),
  trace: z.array(DiscountStackTraceEntrySchema).nullable(),
});
export type DraftLineStack = z.infer<typeof DraftLineStackSchema>;

export const GetDraftLineStacksInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const GetDraftLineStacksResponseSchema = z.object({
  found: z.boolean(),
  stacks: z.array(DraftLineStackSchema),
});

export const ApplyLineStackInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  draftLineId: UUIDSchema,
  /** Elements to stack; the engine assigns the canonical application order. */
  elementIds: z.array(UUIDSchema).min(1),
});

export const ApplyLineStackResponseSchema = z.object({
  stack: DraftLineStackSchema,
});

/**
 * Batch apply: one call for a whole order's worth of stacks (the bulk-discount
 * flow). Applying line-by-line would emit one approval-recheck event per line
 * — the whole approver team got re-notified once per line item.
 */
export const ApplyLineStacksInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  applications: z
    .array(
      z.object({
        draftLineId: UUIDSchema,
        elementIds: z.array(UUIDSchema).min(1),
      }),
    )
    .min(1),
});

export const ApplyLineStacksResponseSchema = z.object({
  /** Per-line outcomes, in input order; a failed line never fails the batch. */
  results: z.array(
    z.object({
      draftLineId: UUIDSchema,
      success: z.boolean(),
      errorMessage: z.string().nullable(),
    }),
  ),
});

export const RemoveLineStackInputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
  draftLineId: UUIDSchema,
});

// ─── Per-line validation (review page status column) ───────────────────────

export const DraftLineDiscountValidationV2Schema = z.object({
  draftLineId: UUIDSchema,
  status: DraftLineDiscountStatusV2Schema,
  listPrice: DecimalStringSchema.nullable(),
  poUnitPrice: DecimalStringSchema.nullable(),
  /** Final unit price the applied stack yields, when a stack exists. */
  stackFinalUnitPrice: DecimalStringSchema.nullable(),
  draftLineDiscountId: UUIDSchema.nullable(),
});
export type DraftLineDiscountValidationV2 = z.infer<
  typeof DraftLineDiscountValidationV2Schema
>;

export const ValidateDraftDiscountsV2InputSchema = z.object({
  draftSalesOrderId: UUIDSchema,
});

export const ValidateDraftDiscountsV2ResponseSchema = z.object({
  found: z.boolean(),
  results: z.record(z.string(), DraftLineDiscountValidationV2Schema),
});

// ─── Analytics export (Discounts page "Export CSV") ─────────────────────────

export const DiscountAnalyticsDatasetSchema = z.enum([
  "summary",
  "groups",
  "elements",
  "usage",
]);
export type DiscountAnalyticsDataset = z.infer<
  typeof DiscountAnalyticsDatasetSchema
>;

/**
 * Discount creation / usage analytics for CSV export.
 *
 * "Used" means frozen at submission — both sources are read:
 * `ar_sales_order_line_discounts` (built-in ERP path) and the
 * `frozen_discounts` snapshot on `ar_draft_sales_order_submissions`
 * (approve-without-ERP / registered-API paths). Stacks still applied on
 * unsubmitted drafts are NOT counted. `from` / `to` bound the *applied* date
 * (the freeze timestamp, compared as UTC calendar days). Groups / elements
 * rows list everything created regardless of the range; only their usage
 * aggregates and the summary honour it. Only the requested dataset's array is
 * populated; the summary always rides along.
 */
export const ExportDiscountAnalyticsInputSchema = z.object({
  dataset: DiscountAnalyticsDatasetSchema,
  from: DateOnlySchema.optional(),
  to: DateOnlySchema.optional(),
  customerId: UUIDSchema.optional(),
});
export type ExportDiscountAnalyticsInput = z.infer<
  typeof ExportDiscountAnalyticsInputSchema
>;

/**
 * Usage aggregates shared by group + element rows. `discountValueGiven` sums
 * the per-step amounts `(unitPriceBefore − unitPriceAfter) × quantity`, which
 * can differ from the once-rounded stored line totals by cents.
 */
const DiscountUsageAggregateFields = {
  /** Element-on-line applications. */
  applications: z.number().int(),
  linesDiscounted: z.number().int(),
  ordersDiscounted: z.number().int(),
  quantityDiscounted: DecimalStringSchema,
  discountValueGiven: DecimalStringSchema,
  firstAppliedAt: z.iso.datetime().nullable(),
  lastAppliedAt: z.iso.datetime().nullable(),
};

export const DiscountAnalyticsSummarySchema = z.object({
  from: DateOnlySchema.nullable(),
  to: DateOnlySchema.nullable(),
  customerId: UUIDSchema.nullable(),
  /** Groups / elements currently existing (soft-deleted ones excluded). */
  groupsCreated: z.number().int(),
  elementsCreated: z.number().int(),
  groupsUsed: z.number().int(),
  elementsUsed: z.number().int(),
  applications: z.number().int(),
  linesDiscounted: z.number().int(),
  ordersDiscounted: z.number().int(),
  quantityDiscounted: DecimalStringSchema,
  /** Sum of stored per-line totals (rounded once at submission). */
  totalDiscountValue: DecimalStringSchema,
});
export type DiscountAnalyticsSummary = z.infer<
  typeof DiscountAnalyticsSummarySchema
>;

export const DiscountGroupAnalyticsRowSchema = z.object({
  groupId: UUIDSchema,
  name: z.string(),
  discountCode: z.string().nullable(),
  referenceCode: z.string().nullable(),
  status: DiscountGroupStatusSchema,
  customerName: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  elementCount: z.number().int(),
  activeElementCount: z.number().int(),
  elementsUsed: z.number().int(),
  ...DiscountUsageAggregateFields,
});
export type DiscountGroupAnalyticsRow = z.infer<
  typeof DiscountGroupAnalyticsRowSchema
>;

export const DiscountElementAnalyticsRowSchema = z.object({
  elementId: UUIDSchema,
  groupId: UUIDSchema.nullable(),
  groupName: z.string().nullable(),
  discountCode: z.string().nullable(),
  groupStatus: DiscountGroupStatusSchema.nullable(),
  discountType: DiscountElementTypeSchema,
  /** The configured value (bps / amount / fixed price). */
  discountValue: DecimalStringSchema,
  targetType: DiscountElementTargetTypeSchema,
  targetValue: z.string().nullable(),
  /** SKU for variant targets, the segment itself otherwise. */
  targetLabel: z.string().nullable(),
  customerName: z.string().nullable(),
  effectiveStart: DateOnlySchema.nullable(),
  effectiveEnd: DateOnlySchema.nullable(),
  isActive: z.boolean(),
  isSingleUse: z.boolean(),
  isConsumed: z.boolean(),
  maxQuantity: DecimalStringSchema.nullable(),
  usedQuantity: DecimalStringSchema,
  createdAt: z.iso.datetime(),
  ...DiscountUsageAggregateFields,
});
export type DiscountElementAnalyticsRow = z.infer<
  typeof DiscountElementAnalyticsRowSchema
>;

export const DiscountUsageSourceSchema = z.enum(["sales_order", "submission"]);

/** One element application: one element frozen onto one submitted line. */
export const DiscountUsageAnalyticsRowSchema = z.object({
  appliedAt: z.iso.datetime(),
  source: DiscountUsageSourceSchema,
  draftSalesOrderId: UUIDSchema.nullable(),
  salesOrderId: UUIDSchema.nullable(),
  salesOrderInternalId: z.number().int().nullable(),
  erpOrderNumber: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  orderDate: DateOnlySchema.nullable(),
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  lineNumber: z.number().int().nullable(),
  sku: z.string().nullable(),
  quantity: DecimalStringSchema.nullable(),
  listPrice: DecimalStringSchema.nullable(),
  finalUnitPrice: DecimalStringSchema,
  /** Stored per-line total (rounded once at submission). */
  lineDiscountAmount: DecimalStringSchema,
  elementId: UUIDSchema,
  /** The element has since been soft-deleted (its group still resolves). */
  elementDeleted: z.boolean(),
  groupId: UUIDSchema.nullable(),
  groupName: z.string().nullable(),
  discountCode: z.string().nullable(),
  discountType: DiscountElementTypeSchema,
  discountValue: DecimalStringSchema,
  applicationOrder: z.number().int(),
  unitPriceBefore: DecimalStringSchema,
  unitPriceAfter: DecimalStringSchema,
  /** (unitPriceBefore − unitPriceAfter) × quantity, rounded to 2dp. */
  elementDiscountAmount: DecimalStringSchema,
});
export type DiscountUsageAnalyticsRow = z.infer<
  typeof DiscountUsageAnalyticsRowSchema
>;

export const ExportDiscountAnalyticsResponseSchema = z.object({
  summary: DiscountAnalyticsSummarySchema,
  groups: z.array(DiscountGroupAnalyticsRowSchema),
  elements: z.array(DiscountElementAnalyticsRowSchema),
  usage: z.array(DiscountUsageAnalyticsRowSchema),
  /** The requested dataset exceeded the row cap and was cut. */
  truncated: z.boolean(),
});
export type ExportDiscountAnalyticsResponse = z.infer<
  typeof ExportDiscountAnalyticsResponseSchema
>;

// ─── Contracts ───────────────────────────────────────────────────────────────

export const discountsV2Contracts = {
  listGroups: oc
    .input(ListDiscountGroupsInputSchema)
    .output(ListDiscountGroupsResponseSchema)
    .route({
      summary: "List discount groups",
      description:
        "Search discount groups (price lists, bulk batches, special pricing grants) by name, status, customer or creator, with sorting, pagination and element counts.",
      method: "GET",
      path: "/groups",
    }),
  getGroup: oc
    .input(GetDiscountGroupInputSchema)
    .output(DiscountGroupV2DetailSchema)
    .route({
      summary: "Get discount group details",
      description:
        "Return one discount group with its elements, review fix-up state for ingested requests, and the requester's email address when it came from an email.",
      method: "GET",
      path: "/group",
    }),
  getActivity: oc
    .input(GetDiscountGroupInputSchema)
    .output(GetDiscountGroupActivityResponseSchema)
    .route({
      summary: "Get discount group activity history",
      description:
        "Return the timeline of recorded actions on a discount group: creation, edits with before/after values, status changes and who made them.",
      method: "GET",
      path: "/group/activity",
    }),
  // Source-email descriptor for the review page's Emails rail tab. Resolves
  // the group (org-scoped) → its source InboundEmail, returning the
  // conversationId the existing email-thread viewer fetches the full thread
  // with, plus the sender + subject to seed a reply. hasSourceEmail is false
  // for groups not created from an email (quote builder, manual entry).
  getSourceEmailThread: oc
    .input(GetDiscountGroupInputSchema)
    .output(GroupSourceEmailThreadSchema)
    .route({
      summary: "Get a discount group's source email",
      description:
        "Return the inbound email a discount group was ingested from (thread id, sender, subject), or indicate the group was not created from email.",
      method: "GET",
      path: "/group/source-email-thread",
    }),
  getUsage: oc
    .input(GetDiscountUsageInputSchema)
    .output(GetDiscountUsageResponseSchema)
    .route({
      summary: "List sales orders using a discount",
      description:
        "Return the submitted sales orders a discount group or single element was applied to, with customer, PO number, order date, total and line count.",
      method: "GET",
      path: "/usage",
    }),
  listElements: oc
    .input(ListDiscountElementsInputSchema)
    .output(ListDiscountElementsResponseSchema)
    .route({
      summary: "List discount elements",
      description:
        "Search individual discount elements across groups by SKU, segment or group name, customer, active status, discount type and product family/group/model. Paginated.",
      method: "GET",
      path: "/elements",
    }),
  createGroup: oc
    .input(CreateDiscountGroupInputSchema)
    .output(CreateDiscountGroupResponseSchema)
    .route({
      summary: "Create a discount group",
      description:
        "Create a discount group with its elements, left in draft, submitted for approval, or approved immediately with active elements.",
      method: "POST",
      path: "/groups",
    }),
  decideGroup: oc
    .input(DecideDiscountGroupInputSchema)
    .output(DecideDiscountGroupResponseSchema)
    .route({
      summary: "Approve or reject a discount group",
      description:
        "Directly approve or reject a discount group as a user holding approve permission; approval activates its elements. Returns new status and activated element count.",
      method: "POST",
      path: "/group/decide",
    }),
  approveAndEmail: oc
    .input(ApproveAndEmailDiscountGroupInputSchema)
    .output(ApproveAndEmailDiscountGroupResponseSchema)
    .route({
      summary: "Approve discount group and email requester",
      description:
        "Approve a discount group, then email the approved form to the original requester from the discounts mailbox, Cc'ing the approver. Fails without approving if email isn't configured.",
      method: "POST",
      path: "/groups/approve-and-email",
    }),
  submitGroupForApproval: oc
    .input(SubmitGroupForApprovalInputSchema)
    .output(SubmitGroupForApprovalResponseSchema)
    .route({
      summary: "Submit discount group for approval",
      description:
        "Move a draft or rejected discount group to pending approval, starting the group approval workflow that routes and notifies approvers.",
      method: "POST",
      path: "/groups/submit-for-approval",
    }),
  deactivateGroup: oc
    .input(DeactivateDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Deactivate a discount group",
      description:
        "Deactivate every element in a discount group so none apply to new orders; the group's approval status is unchanged.",
      method: "POST",
      path: "/groups/deactivate",
    }),
  deleteGroup: oc
    .input(DeleteDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Delete a discount group",
      description:
        "Soft-delete a draft or rejected discount group whose elements were never applied or consumed; anything used must be deactivated instead.",
      method: "DELETE",
      path: "/groups",
    }),
  deactivateElement: oc
    .input(DeactivateDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Deactivate a discount element",
      description:
        "Turn off a single discount element so it no longer applies to orders.",
      method: "POST",
      path: "/elements/deactivate",
    }),
  addElement: oc
    .input(AddDiscountElementInputSchema)
    .output(AddDiscountElementResponseSchema)
    .route({
      summary: "Add element to discount group",
      description:
        "Add one discount element (type, value, target, customer, dates, quantity cap) to a draft, rejected or pending discount group.",
      method: "POST",
      path: "/elements/add",
    }),
  updateElement: oc
    .input(UpdateDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Update a discount element",
      description:
        "Edit one element of an editable discount group, unless it is already applied to an order line.",
      method: "POST",
      path: "/elements/update",
    }),
  deleteElement: oc
    .input(DeleteDiscountElementInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Remove element from discount group",
      description:
        "Soft-delete one element from an editable discount group; refused when the element has been applied.",
      method: "DELETE",
      path: "/elements",
    }),
  updateGroup: oc
    .input(UpdateDiscountGroupInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Update discount group details",
      description:
        "Edit an editable discount group's name, description, reference code or customer; assigning a customer resolves review questions about it.",
      method: "POST",
      path: "/groups/update",
    }),
  revertGroupToDraft: oc
    .input(RevertDiscountGroupToDraftInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Revert discount group to draft",
      description:
        "Return an unused approved or pending discount group to draft for editing, deactivating its elements until re-approved. Refused once any element was applied or consumed.",
      method: "POST",
      path: "/groups/revert-to-draft",
    }),
  exportAnalytics: oc
    .input(ExportDiscountAnalyticsInputSchema)
    .output(ExportDiscountAnalyticsResponseSchema)
    .route({
      summary: "Export discount analytics",
      description:
        "Export discount summary, group, element or usage data for CSV, with usage bounded by an applied-date range and optionally one customer.",
      method: "GET",
      path: "/analytics-export",
    }),
  getRequestFormTemplate: oc.output(DiscountFormFileSchema).route({
    summary: "Download the discount request form",
    description:
      "Return the blank standardized discount request Excel form as a base64 file.",
    method: "GET",
    path: "/request-form-template",
  }),
  generateApprovedForm: oc
    .input(GenerateApprovedDiscountFormInputSchema)
    .output(GenerateApprovedDiscountFormResponseSchema)
    .route({
      summary: "Generate approved discount form",
      description:
        "Build the approved-discount Excel form for a group, save it as a document on the group, and return it with a suggested recipient email.",
      method: "POST",
      path: "/groups/approved-form",
    }),
  resolveUnresolvedLine: oc
    .input(ResolveUnresolvedLineInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Match an ingested discount line to a product",
      description:
        "Resolve an unmatched row on an ingested discount group by choosing its product, creating an inactive element that activates on approval.",
      method: "POST",
      path: "/groups/resolve-line",
    }),
  dismissUnresolvedLine: oc
    .input(DismissUnresolvedLineInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Dismiss an ingested discount line",
      description:
        "Drop an unresolved row from an ingested discount group's review list; it is kept as dismissed for audit.",
      method: "POST",
      path: "/groups/dismiss-line",
    }),
  resolveUnresolvedLinePrice: oc
    .input(ResolveUnresolvedLinePriceInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Set price for an ingested discount line",
      description:
        "Give a product-matched but priceless row on an ingested discount group its fixed unit price, turning it into a fixed-price element.",
      method: "POST",
      path: "/groups/resolve-line-price",
    }),
  previewPriceListUpload: oc
    .input(PreviewPriceListUploadInputSchema)
    .output(PreviewPriceListUploadResponseSchema)
    .route({
      summary: "Preview uploaded price list columns",
      description:
        "Return the sheets, headers, sample rows and suggested SKU and price columns of a spreadsheet you uploaded, for picking columns before import.",
      method: "POST",
      path: "/groups/preview-price-list",
    }),
  parsePriceListUpload: oc
    .input(ParsePriceListUploadInputSchema)
    .output(ParsePriceListUploadResponseSchema)
    .route({
      summary: "Import price list into discount group",
      description:
        "Create a discount group from an uploaded price-list spreadsheet using chosen or AI-detected SKU and price columns; returns counts and unmatched SKUs. Optionally submits or approves.",
      method: "POST",
      path: "/groups/parse-price-list",
    }),
  listDraftCandidates: oc
    .input(ListDraftCandidatesInputSchema)
    .output(ListDraftCandidatesResponseSchema)
    .route({
      summary: "List discounts eligible for draft order lines",
      description:
        "For each line of a draft sales order, return the discount elements that could apply and the one that would be auto-applied.",
      method: "GET",
      path: "/draft-candidates",
    }),
  getDraftLineStacks: oc
    .input(GetDraftLineStacksInputSchema)
    .output(GetDraftLineStacksResponseSchema)
    .route({
      summary: "Get discounts applied to draft order lines",
      description:
        "Return the discount stacks applied to a draft sales order's lines, with final unit prices, pending-approval flags and step-by-step price traces.",
      method: "GET",
      path: "/draft-line-stacks",
    }),
  applyLineStack: oc
    .input(ApplyLineStackInputSchema)
    .output(ApplyLineStackResponseSchema)
    .route({
      summary: "Apply discounts to a draft order line",
      description:
        "Apply a stack of discount elements to one draft sales-order line, then re-evaluate the order's affected approvals. Returns the resulting stack.",
      method: "POST",
      path: "/apply-line-stack",
    }),
  applyLineStacks: oc
    .input(ApplyLineStacksInputSchema)
    .output(ApplyLineStacksResponseSchema)
    .route({
      summary: "Apply discounts to many draft order lines",
      description:
        "Apply discount stacks to several lines of one draft sales order in one call, re-evaluating approvals once. Returns per-line success.",
      method: "POST",
      path: "/apply-line-stacks",
    }),
  removeLineStack: oc
    .input(RemoveLineStackInputSchema)
    .output(SimpleSuccessResponseSchema)
    .route({
      summary: "Remove discounts from a draft order line",
      description:
        "Remove the applied discount stack from one draft sales-order line and re-evaluate the order's affected approvals.",
      method: "POST",
      path: "/remove-line-stack",
    }),
  validateDraftDiscounts: oc
    .input(ValidateDraftDiscountsV2InputSchema)
    .output(ValidateDraftDiscountsV2ResponseSchema)
    .route({
      summary: "Check discount status of draft order lines",
      description:
        "Return each draft sales-order line's discount status with list price, PO unit price and the applied stack's final unit price.",
      method: "GET",
      path: "/validate-draft",
    }),
};
